/**
 * sheetJoin.ts
 * Looks up extra columns from a secondary sheet and attaches them to the rows
 * of the primary sheet.
 *
 * Only one sheet is ever bound directly (the primary sheet chosen in step 1).
 * Secondary sheets are pure lookups: a row is matched through a join key that
 * the user confirms, and the borrowed columns are namespaced as
 * `<sheet>.<column>` so they never collide with the primary headers.
 */

import { normalizeHeader } from "@/utils/columnMatcher";
import { isRowMetaKey } from "@/types/excel";

/** Separates the sheet name from the column name in a borrowed field. */
export const SHEET_FIELD_SEPARATOR = ".";

export function qualifyColumn(sheetName: string, column: string): string {
  return `${sheetName}${SHEET_FIELD_SEPARATOR}${column}`;
}

export type JoinWarningKind = "no-match" | "multi-match";

export interface JoinWarning {
  kind: JoinWarningKind;
  /** The join value that failed to resolve to exactly one row. */
  keyValue: string;
  /** Excel row number of the primary row, when available. */
  rowIndex?: number;
  /** How many secondary rows matched (for multi-match). */
  matchCount?: number;
}

export interface SheetLike {
  name: string;
  headers: string[];
  rows: Record<string, unknown>[];
}

export interface JoinOptions {
  /** Column on the primary sheet holding the join value. */
  primaryKey: string;
  /** Column on the secondary sheet holding the join value. */
  secondaryKey: string;
  /** Columns to borrow; defaults to every secondary column except the key. */
  columns?: string[];
}

export interface JoinResult {
  /** Primary rows with the borrowed columns attached. */
  rows: Record<string, unknown>[];
  /** Qualified names of the columns that were added. */
  addedColumns: string[];
  warnings: JoinWarning[];
}

/** Normalized comparison so "PO 001" and "po-001" join. */
function joinValueOf(row: Record<string, unknown>, column: string): string {
  const raw = row[column];
  if (raw == null) return "";
  return normalizeHeader(String(raw));
}

/**
 * Columns that exist on both sheets (compared after normalization) and are
 * therefore plausible join keys. The user still has to confirm one.
 */
export function suggestJoinKeys(primaryHeaders: string[], secondaryHeaders: string[]): string[] {
  const secondaryNormalized = new Set(secondaryHeaders.map(normalizeHeader));
  return primaryHeaders.filter(
    (h) => !isRowMetaKey(h) && secondaryNormalized.has(normalizeHeader(h))
  );
}

/** The secondary column matching a primary column name, if any. */
export function counterpartColumn(column: string, secondaryHeaders: string[]): string | undefined {
  const target = normalizeHeader(column);
  return secondaryHeaders.find((h) => normalizeHeader(h) === target);
}

/**
 * Attach columns from a secondary sheet to every primary row.
 *
 * Exactly one match fills the borrowed columns. Several matches is a 1–n
 * relationship that cannot be collapsed into a single value, so the columns
 * stay empty and the caller gets a warning to resolve. No match is likewise
 * reported rather than silently blank.
 */
export function joinSheets(
  primaryRows: Record<string, unknown>[],
  secondary: SheetLike,
  options: JoinOptions
): JoinResult {
  const { primaryKey, secondaryKey } = options;
  const borrowed = (
    options.columns ?? secondary.headers.filter((h) => h !== secondaryKey)
  ).filter((h) => !isRowMetaKey(h));
  const addedColumns = borrowed.map((c) => qualifyColumn(secondary.name, c));

  if (!primaryKey || !secondaryKey) {
    return { rows: primaryRows, addedColumns: [], warnings: [] };
  }

  const index = new Map<string, Record<string, unknown>[]>();
  for (const row of secondary.rows) {
    const key = joinValueOf(row, secondaryKey);
    if (!key) continue;
    const bucket = index.get(key);
    if (bucket) bucket.push(row);
    else index.set(key, [row]);
  }

  const warnings: JoinWarning[] = [];
  const rows = primaryRows.map((row) => {
    const key = joinValueOf(row, primaryKey);
    const matches = key ? index.get(key) ?? [] : [];
    const merged: Record<string, unknown> = { ...row };

    for (const column of borrowed) {
      merged[qualifyColumn(secondary.name, column)] =
        matches.length === 1 ? matches[0][column] ?? null : null;
    }

    if (matches.length === 0) {
      warnings.push({
        kind: "no-match",
        keyValue: String(row[primaryKey] ?? ""),
        rowIndex: typeof row.__rowIndex === "number" ? row.__rowIndex : undefined,
      });
    } else if (matches.length > 1) {
      warnings.push({
        kind: "multi-match",
        keyValue: String(row[primaryKey] ?? ""),
        rowIndex: typeof row.__rowIndex === "number" ? row.__rowIndex : undefined,
        matchCount: matches.length,
      });
    }

    return merged;
  });

  return { rows, addedColumns, warnings };
}

/** Short human summary of the join warnings, for the mapping step. */
export function summarizeJoinWarnings(warnings: JoinWarning[]): string[] {
  const noMatch = warnings.filter((w) => w.kind === "no-match");
  const multi = warnings.filter((w) => w.kind === "multi-match");
  const messages: string[] = [];
  if (noMatch.length > 0) {
    messages.push(
      `${noMatch.length} dòng không tìm thấy bản ghi nối (ví dụ khóa "${noMatch[0].keyValue}") — các trường lấy từ sheet phụ sẽ để trống.`
    );
  }
  if (multi.length > 0) {
    messages.push(
      `${multi.length} dòng khớp nhiều bản ghi ở sheet phụ (ví dụ khóa "${multi[0].keyValue}" khớp ${multi[0].matchCount} dòng) — cần xác nhận, hệ thống không tự chọn dòng.`
    );
  }
  return messages;
}
